import React, { useState } from "react";
import CameraSelect, { Camera } from "./CameraSelect";
import ParameterInput from "./ParameterInput";
import Modal from "react-modal";
import ReactGA from "react-ga4";

export type CameraParameters = {
  camera: Camera;
  exposureTime: number;
};

const earthPhotos = [
  "photos/biwako.jpg",
  "photos/fujisan.jpg",
  "photos/newyork.jpg",
  "photos/parana.jpg",
  "photos/saintpablo.jpg",
] as const;

const horizonPhotos = [
  "photos/earth.jpg",
  "photos/earth_and_moon.jpg",
  "photos/earth_and_typhoon.jpg",
] as const;

const spacePhotos = ["background.jpg", "photos/stars.jpg"] as const;

const selectedPhotos = (camera: Camera) => {
  if (camera === "earth") {
    return earthPhotos;
  } else if (camera === "horizon") {
    return horizonPhotos;
  } else if (camera === "space") {
    return spacePhotos;
  } else {
    throw Error("unreachable");
  }
};

const customStyles = {
  content: {
    height: "80%",
    margin: "auto",
    borderRadius: "20px",
    background: "#FFFFCF",
    color: "black",
  },
};

const EXPOSURE_OFF = 0;
const EXPOSURE_AUTO = 1;
const EXPOSURE_NIGHT = 2;
const EXPOSURE_NIGHT_PREVIEW = 3;
const EXPOSURE_BACKLIGHT = 4;
const EXPOSURE_SPOTLIGHT = 5;
const EXPOSURE_SPORTS = 6;
const EXPOSURE_SNOW = 7;
const EXPOSURE_BEACH = 8;
const EXPOSURE_VERY_LONG = 9;
const EXPOSURE_FIXED_FPS = 10;
const EXPOSURE_ANTI_SHAKE = 11;
const EXPOSURE_FIREWORKS = 12;
/**
 * これらの値は『趣味で作る人工衛星』を参考にした。
 *
 * これらの値はラズパイのカメラの設定から来ているようだ
 */
const EXPOSURES = [
  { key: "オフ", value: EXPOSURE_OFF },
  { key: "自動", value: EXPOSURE_AUTO },
  { key: "ナイト", value: EXPOSURE_NIGHT },
  { key: "ナイトプレビュー", value: EXPOSURE_NIGHT_PREVIEW },
  { key: "逆光", value: EXPOSURE_BACKLIGHT },
  { key: "スポットライト", value: EXPOSURE_SPOTLIGHT },
  { key: "スポーツ", value: EXPOSURE_SPORTS },
  { key: "雪", value: EXPOSURE_SNOW },
  { key: "ビーチ", value: EXPOSURE_BEACH },
  { key: "長時間", value: EXPOSURE_VERY_LONG },
  { key: "固定FPS", value: EXPOSURE_FIXED_FPS },
  { key: "ぶれ防止", value: EXPOSURE_ANTI_SHAKE },
  { key: "花火", value: EXPOSURE_FIREWORKS },
];

type Exposure = (typeof EXPOSURES)[number]["value"];

/**
 * カメラの設定項目は『趣味で作る人工衛星』を参考にした。
 *
 * ただし、これらの項目はラズバイのカメラの設定項目なので、SPRESENCEにそのまま使えるかどうかは不明。
 *
 * SPRESENCEのカメラライブラリには、似ているが少し違う設定項目があるように思われる。
 */
const Controller = () => {
  const [camera, setCamera] = useState<Camera>("earth");
  const [isoSensitivity, setIsoSensitivity] = useState(400);
  const [shutterSpeed, setShutterSpeed] = useState(1000);
  const [jpegQuality, setJpegQuality] = useState(90);
  const [sharpness, setSharpness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [brightness, setBrightness] = useState(50);
  const [saturation, setSaturation] = useState(50);
  const [exposure, setExposure] = useState<Exposure>(EXPOSURE_AUTO);
  const [photo, setPhoto] = useState<string | undefined>(undefined);

  return (
    <div id="controller">
      <div
        className="table"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <CameraSelect camera={camera} setCamera={setCamera} />
          <ParameterInput
            label="ISO感度"
            min={100}
            max={800}
            value={isoSensitivity}
            setValue={setIsoSensitivity}
          />
          <ParameterInput
            label="シャッタースピード[μs]"
            min={0}
            max={6000000}
            value={shutterSpeed}
            setValue={setShutterSpeed}
          />
          <ParameterInput
            label="JPEG品質"
            min={0}
            max={100}
            value={jpegQuality}
            setValue={setJpegQuality}
          />
          <ParameterInput
            label="シャープネス"
            min={100}
            max={156}
            value={sharpness}
            setValue={setSharpness}
          />
          <ParameterInput
            label="コントラスト"
            min={100}
            max={156}
            value={contrast}
            setValue={setContrast}
          />
          <ParameterInput
            label="明るさ"
            min={0}
            max={100}
            value={brightness}
            setValue={setBrightness}
          />
          <ParameterInput
            label="彩度"
            min={0}
            max={100}
            value={saturation}
            setValue={setSaturation}
          />
          <p className="textBox cell cp_ipselect cp_sl03">
            <select
              value={exposure}
              name="exposure"
              id="exposure"
              onChange={(event) => {
                const newExposure = event.target.value;
                ReactGA.event({
                  category: "controller",
                  action: "exposure change",
                  label: event.target.options[event.target.selectedIndex].text,
                });
                setExposure(parseInt(newExposure, 10) as Exposure);
              }}
            >
              {EXPOSURES.map((item) => (
                <option value={item.value} key={`exposure-${item.value}`}>
                  {item.key}
                </option>
              ))}
            </select>
          </p>
        </div>

        <button
          className={"btn btn--yellow btn--cubic"}
          onClick={() => {
            ReactGA.event({
              category: "controller",
              action: "camera button press",
              label: camera,
            });
            const photos = selectedPhotos(camera);
            setPhoto(photos[Math.floor(Math.random() * photos.length)]);
          }}
        >
          衛星にコマンド送信
        </button>
        <p className="notice">
          ※まだ実際の人工衛星は打ち上げてはいません。
          <br />
          実際にコマンドが送信されることはありません。
          <br />
          パラメータを設定しても現状は機能的な違いはありません。
          <br />
          <strong>
            今後実現する機能に関して、皆様の
            <a
              className="link"
              href="https://docs.google.com/forms/d/e/1FAIpQLSdvG4wIyI_F8IhPfLbHak-V0rxiQXieABv62LqBMqwiGzvMVA/viewform?usp=sf_link"
            >
              ご意見・ご感想
            </a>
            を求めています。
          </strong>
          <br />
          <strong>
            宇宙開発に関するオウンドメディア
            <a className="link" href="https://techsource.site/">
              TechSource
            </a>
            もよろしくお願いします。
          </strong>
        </p>
      </div>
      <Modal
        isOpen={photo !== undefined}
        onRequestClose={() => setPhoto(undefined)}
        style={customStyles}
        contentLabel="result modal"
      >
        <h2>撮影結果</h2>
        <p>※この写真は仮のものです。実際に撮影したわけではありません。</p>
        <img src={photo} alt={"仮の衛星写真"} width="100%" />
        <p>
          写真は
          <a href="https://publicdomainq.net/">
            パブリックドメインの写真を調べるサイト
          </a>
          から選びました。
        </p>
        <p>
          今後の方針決めのために、
          <a
            className="link"
            href="https://docs.google.com/forms/d/e/1FAIpQLSdvG4wIyI_F8IhPfLbHak-V0rxiQXieABv62LqBMqwiGzvMVA/viewform?usp=sf_link"
          >
            アンケート
          </a>
          に答えてくださると嬉しいです。
        </p>
        <p>
          <button onClick={() => setPhoto(undefined)}>閉じる</button>
        </p>
      </Modal>
    </div>
  );
};

export default Controller;
